import { useRef, useState } from "react";
import { Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import type { TicketAttachment } from "@/lib/api";

const MAX_ATTACHMENTS = 3;
const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB — même limite que le système existant

type Props = {
  value: TicketAttachment[];
  onChange: (next: TicketAttachment[]) => void;
  disabled?: boolean;
};

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

export function AttachmentPicker({ value, onChange, disabled = false }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    const room = MAX_ATTACHMENTS - value.length;
    if (room <= 0) {
      setError(`Maximum ${MAX_ATTACHMENTS} fichiers par message`);
      return;
    }

    const accepted: TicketAttachment[] = [];
    for (const f of Array.from(files).slice(0, room)) {
      if (f.size > MAX_FILE_BYTES) {
        setError(`${f.name} : fichier trop gros (max 2 MB)`);
        continue;
      }
      const data_url = await readAsDataUrl(f);
      accepted.push({
        name: f.name,
        type: f.type || "application/octet-stream",
        size: f.size,
        data_url,
      });
    }
    onChange([...value, ...accepted]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const canAdd = !disabled && value.length < MAX_ATTACHMENTS;

  return (
    <div>
      {value.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {value.map((att, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#f5f8fa] border border-[#eff2f5]"
            >
              {att.type.startsWith("image/") ? (
                <ImageIcon size={14} className="text-brand-700 shrink-0" />
              ) : (
                <FileText size={14} className="text-[#5e6278] shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[#181c32] truncate">{att.name}</div>
                <div className="text-[10px] text-[#7e8299]">{formatBytes(att.size)}</div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-[#7e8299] hover:text-danger-500 shrink-0"
                  aria-label="Retirer"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!canAdd}
          onClick={() => fileRef.current?.click()}
          className="btn btn-light btn-sm"
        >
          <Paperclip size={13} />
          Joindre un fichier
        </button>
        <div className="text-[11px] text-[#7e8299]">
          {value.length}/{MAX_ATTACHMENTS} — max 2 MB par fichier
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handlePick(e.target.files)}
      />
      {error && <div className="text-xs text-danger-700 mt-2">{error}</div>}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
